/**
 * WhatsApp chatbot flow builder (React + reactflow + RTK Query)
 *
 * Built to match backend BOT.md. Things to know:
 *  - Start = messageCard with isStart:true. It sends a real welcome message (no keyword trigger).
 *  - Buttons / List ids are stable (never shift when you delete an option) and are saved as
 *    edge.data.reply_id + edge.sourceHandle.
 *  - Condition has ONE output (BOT.md: "if the condition fails, that branch stops").
 *  - Business hours has Open / Closed outputs. BOT.md does not name those edge ids, so they are
 *    HOURS_OPEN / HOURS_CLOSED below. Confirm with backend and change if needed.
 *  - Steps this builder can't edit (caseSwitchCard, scriptCard, imageCard, ...) are kept as-is
 *    ("Advanced step") so saving never silently deletes them.
 *  - Active / inactive: "My flows" shows the status of every flow with a switch. Only one flow
 *    per number can be active (turning one on turns the others off on the server).
 *  - URL params supported: ?customer_id=&phone_number_id=&workflow_id=
 */
import React, { useState, useRef, useCallback, useEffect, useMemo, useContext, createContext } from "react";
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MarkerType,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  useWhatsappchatbotcreateMutation,
  useGetwhatsappchatflowlistQuery,
  useLazyGetPhoneNUmberQuery,
  useSelectwhatsappchatflowQuery,
  useLazySelectwhatsappchatflowQuery,
  useGetTemplatesQuery,
} from "../store/services/whatsappApi";
import { jwtDecode } from "jwt-decode";

/* ───────────────────────── constants ───────────────────────── */
const LIMITS = { buttons: 3, buttonTitle: 20, rows: 10, rowTitle: 24, rowDesc: 72, listButton: 20 };
const HOURS_OPEN = "open";
const HOURS_CLOSED = "closed";
const EDGE_COLOR = "#6f817b";

const CONDITIONS = [
  ["message_content", "What the customer wrote"],
  ["reply", "The button or list item they tapped"],
  ["message_type", "Type of message (text, image…)"],
  ["sender", "Their phone number"],
  ["variable", "A saved answer"],
  ["has_session", "They are already inside a flow"],
  ["is_assigned_to_agent", "A human agent has this chat"],
];
const YES_NO = ["has_session", "is_assigned_to_agent"];
const OPERATORS = [
  ["equals", "is exactly"],
  ["not_equals", "is not"],
  ["contains", "contains"],
  ["not_contains", "does not contain"],
  ["starts_with", "starts with"],
  ["ends_with", "ends with"],
  ["is_empty", "is empty"],
  ["is_not_empty", "is not empty"],
  ["regex", "matches a pattern (regex)"],
];
const NO_VALUE = ["is_empty", "is_not_empty"];
const TIMEZONES = ["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "America/New_York", "America/Los_Angeles", "UTC"];
const WEEKDAYS = [["mon-fri", "Monday to Friday"], ["1,2,3,4,5,6", "Monday to Saturday"], ["all", "Every day"]];

const uid = (p) => `${p}_${Math.random().toString(36).slice(2, 8)}`;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9_]/g, "_");
const blank = (v) => !String(v ?? "").trim();
const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== "" && v != null));
const mkOpts = (prefix, titles) => titles.map((title) => ({ id: uid(prefix), title, description: "" }));
const blankTpl = { messageType: "text", templateId: "", templateName: "", templateInfo: null, templateParameters: [] };

/* Each step type: how it looks in the palette, which API node type it saves as, and its defaults */
const KINDS = {
  start: { title: "Start", hint: "The first message customers get", icon: "▶", color: "#0b7a6b", api: "messageCard", group: "Begin",
    init: () => ({ ...blankTpl, text: "Hi! 👋 Thanks for contacting us. How can we help you today?" }) },
  message: { title: "Message", hint: "Send text or a saved template", icon: "💬", color: "#1d6fa5", api: "messageCard", group: "Send",
    init: () => ({ ...blankTpl, text: "" }) },
  buttons: { title: "Buttons", hint: "Ask a question with up to 3 buttons", icon: "🔘", color: "#c2620a", api: "buttonCard", group: "Send",
    init: () => ({ text: "What would you like to do?", options: mkOpts("btn", ["Option 1", "Option 2"]) }) },
  list: { title: "List", hint: "A menu with up to 10 choices", icon: "📋", color: "#7b4fc2", api: "listCard", group: "Send",
    init: () => ({ text: "Please choose from the list.", buttonText: "Choose", sectionTitle: "", options: mkOpts("item", ["Item 1", "Item 2"]) }) },
  ask: { title: "Ask a question", hint: "Wait for a typed answer and save it", icon: "⌨️", color: "#0f8a5f", api: "inputCard", group: "Collect",
    init: () => ({ text: "What's your name?", variableName: "customer_name" }) },
  media: { title: "Ask for a file", hint: "Photo, document, video or audio", icon: "📎", color: "#b4437a", api: "mediaCaptureCard", group: "Collect",
    init: () => ({ text: "Please upload your document.", variableName: "uploaded_file" }) },
  condition: { title: "Check something", hint: "Continue only if a rule is true", icon: "⚡", color: "#5b5fc7", api: "conditionCard", group: "Logic",
    init: () => ({ condition: "message_content", operator: "contains", value: "", variableName: "", regexFlags: "i" }) },
  hours: { title: "Business hours", hint: "Different path when open or closed", icon: "🕘", color: "#5b5fc7", api: "conditionCard", group: "Logic",
    init: () => ({ timezone: "Asia/Kolkata", startTime: "09:00", endTime: "18:00", weekdays: "mon-fri" }) },
  saveValue: { title: "Save a value", hint: "Remember a message or a tapped choice", icon: "📝", color: "#6b7280", api: "variableStore", group: "Logic",
    init: () => ({ variableName: "saved_value", source: "message" }) },
  http: { title: "Call an API", hint: "Send data to another system", icon: "🌐", color: "#0e7490", api: "httpCard", group: "Connect",
    init: () => ({ method: "POST", url: "", headers: [{ k: "Content-Type", v: "application/json" }], bodyText: "{\n  \n}" }) },
  handoff: { title: "Talk to a person", hint: "Pause the bot, an agent takes over", icon: "🙋", color: "#c0392b", api: "handoffCard", group: "Connect",
    init: () => ({ text: "Connecting you to our team…", department: "", subDepartment: "", priority: "normal" }) },
};
const RAW = { title: "Advanced step", icon: "🧩", color: "#6b7280" };
const GROUPS = ["Begin", "Send", "Collect", "Logic", "Connect"];
const mk = (kind, position) => ({ id: uid("node"), type: kind, position, data: KINDS[kind].init() });

/* ───────────────────────── template helpers ───────────────────────── */
const comp = (t, type) => t?.components?.find((c) => c.type === type);
const paramCount = (text) => Math.max(0, ...[...(text || "").matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1])));
const templateInfoOf = (t) => ({
  body: comp(t, "BODY")?.text || "",
  headerFormat: comp(t, "HEADER")?.format || "",
  buttons: (comp(t, "BUTTONS")?.buttons || []).map((b) => b.text),
});

/* ───────────────────────── save / load ───────────────────────── */
function messageData(d) {
  if (d.messageType === "template" && d.templateId) {
    const out = { message_type: "template", template_id: d.templateId };
    const params = (d.templateParameters || []).map((p) => p.trim()).filter(Boolean);
    if (params.length) out.template_parameters = params;
    return out;
  }
  return { text: d.text || "" };
}

function stepData(n) {
  const d = n.data;
  switch (n.type) {
    case "start": return { ...messageData(d), isStart: true };
    case "message": return messageData(d);
    case "buttons": return { text: d.text, buttons: d.options.map((o) => ({ id: o.id, title: o.title })) };
    case "list":
      return clean({
        text: d.text,
        button: d.buttonText,
        section_title: d.sectionTitle,
        items: d.options.map((o) => clean({ id: o.id, title: o.title, description: o.description })),
      });
    case "ask":
    case "media": return { text: d.text, variable_name: d.variableName };
    case "handoff": return clean({ text: d.text, department: d.department, sub_department: d.subDepartment, priority: d.priority });
    case "condition": {
      if (YES_NO.includes(d.condition)) return { condition: d.condition, operator: "equals", value: "true" };
      const out = { condition: d.condition, operator: d.operator };
      if (d.condition === "variable") out.variable_name = d.variableName;
      if (!NO_VALUE.includes(d.operator)) out.value = d.value;
      if (d.operator === "regex") out.regex_flags = d.regexFlags || "i";
      return out;
    }
    case "hours": return { condition: "business_hours", timezone: d.timezone, start_time: d.startTime, end_time: d.endTime, weekdays: d.weekdays };
    case "saveValue": return { variable_name: d.variableName, source: d.source };
    case "http": {
      const headers = Object.fromEntries((d.headers || []).filter((h) => h.k.trim()).map((h) => [h.k.trim(), h.v]));
      const out = { method: d.method, url: d.url.trim(), headers };
      if (d.method !== "GET") {
        try { out.body = JSON.parse(d.bodyText || "{}"); } catch { out.body = d.bodyText; }
      }
      return out;
    }
    default: return d.raw;
  }
}

function buildPayload(name, description, nodes, edges, isActive = true) {
  return {
    name,
    description,
    is_active: isActive,
    workflow_config: {
      nodes: nodes.map((n) => ({
        id: n.id,
        type: n.type === "raw" ? n.data.apiType : KINDS[n.type].api,
        position: { x: Math.round(n.position.x), y: Math.round(n.position.y) },
        data: stepData(n),
      })),
      edges: edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        ...(e.sourceHandle ? { sourceHandle: e.sourceHandle, data: { reply_id: e.sourceHandle } } : {}),
      })),
      viewport: { x: 0, y: 0, zoom: 1 },
    },
  };
}

/* which output connectors a step has (null = the single default output) */
const handleIds = (n) => {
  if (n.type === "buttons" || n.type === "list") return n.data.options.map((o) => o.id);
  if (n.type === "hours") return [HOURS_OPEN, HOURS_CLOSED];
  if (n.type === "handoff") return [];
  return [null];
};

function loadFlow(payload) {
  const cfg = payload?.workflow_config;
  if (!cfg) return null;

  const nodes = (cfg.nodes || []).map((n) => {
    const d = n.data || {};
    const base = { id: n.id, position: n.position || { x: 0, y: 0 } };
    const raw = () => ({ ...base, type: "raw", data: { apiType: n.type, raw: d, extraHandles: [] } });
    const opts = (arr, prefix) => (arr || []).map((o) => ({ id: o.id || uid(prefix), title: o.title || o.label || "", description: o.description || "" }));
    const tpl = d.message_type === "template" && d.template_id
      ? { messageType: "template", templateId: d.template_id, templateName: "", templateInfo: null, templateParameters: d.template_parameters || [] }
      : { ...blankTpl };

    switch (n.type) {
      case "messageCard": return { ...base, type: d.isStart ? "start" : "message", data: { ...tpl, text: d.text || "" } };
      case "buttonCard": return { ...base, type: "buttons", data: { text: d.text || "", options: opts(d.buttons, "btn") } };
      case "listCard":
        return { ...base, type: "list", data: { text: d.text || "", buttonText: d.button || d.button_text || "", sectionTitle: d.section_title || "", options: opts(d.items, "item") } };
      case "inputCard": return { ...base, type: "ask", data: { text: d.text || "", variableName: d.variable_name || "" } };
      case "mediaCaptureCard": return { ...base, type: "media", data: { text: d.text || "", variableName: d.variable_name || "" } };
      case "handoffCard":
        return { ...base, type: "handoff", data: { text: d.text || "", department: d.department || "", subDepartment: d.sub_department || "", priority: d.priority || "normal" } };
      case "conditionCard":
        if (d.condition === "business_hours") {
          return { ...base, type: "hours", data: { timezone: d.timezone || "Asia/Kolkata", startTime: d.start_time || "09:00", endTime: d.end_time || "18:00", weekdays: d.weekdays || "mon-fri" } };
        }
        if (!CONDITIONS.some(([v]) => v === d.condition)) return raw();
        return { ...base, type: "condition", data: { condition: d.condition, operator: d.operator || "contains", value: d.value || "", variableName: d.variable_name || "", regexFlags: d.regex_flags || "i" } };
      case "variableStore":
        if (d.source !== "message" && d.source !== "reply") return raw();
        return { ...base, type: "saveValue", data: { variableName: d.variable_name || "", source: d.source } };
      case "httpCard":
        return {
          ...base,
          type: "http",
          data: {
            method: d.method || "POST",
            url: d.url || "",
            headers: Object.entries(d.headers || {}).map(([k, v]) => ({ k, v: String(v) })),
            bodyText: typeof d.body === "string" ? d.body : JSON.stringify(d.body || {}, null, 2),
          },
        };
      default: return raw();
    }
  });

  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const extra = {};
  let dropped = 0;
  const edges = [];
  (cfg.edges || []).forEach((e) => {
    const src = byId[e.source];
    if (!src || !byId[e.target]) { dropped += 1; return; }
    let h = e.sourceHandle || e.data?.reply_id || null;
    if (src.type === "raw") {
      if (h) (extra[src.id] = extra[src.id] || new Set()).add(h);
    } else {
      if (src.type === "condition" && h === "true") h = null; // old builder: TRUE path = normal path
      if (src.type === "hours" && h === "true") h = HOURS_OPEN;
      if (src.type === "hours" && h === "false") h = HOURS_CLOSED;
      if (!handleIds(src).includes(h)) { dropped += 1; return; }
    }
    edges.push({ id: e.id || uid("edge"), source: e.source, target: e.target, sourceHandle: h });
  });
  nodes.forEach((n) => { if (n.type === "raw") n.data.extraHandles = [...(extra[n.id] || [])]; });
  return { nodes, edges, dropped };
}

/* ───────────────────────── checklist ───────────────────────── */
function inspect(nodes, edges) {
  const errors = [];
  const warnings = [];
  const label = (n) => KINDS[n.type]?.title || "Step";
  const bad = (n, msg) => errors.push({ id: n.id, msg: `${label(n)}: ${msg}` });
  const warn = (n, msg) => warnings.push({ id: n.id, msg: `${label(n)}: ${msg}` });
  const starts = nodes.filter((n) => n.type === "start");
  if (!starts.length) errors.push({ msg: "Add a Start step. It is the first message customers get." });
  starts.slice(1).forEach((n) => bad(n, "Only one Start step is allowed."));
  const hasIn = new Set(edges.map((e) => e.target));
  const leadsOn = (n, h) => edges.some((e) => e.source === n.id && (e.sourceHandle || null) === (h ?? null));

  nodes.forEach((n) => {
    const d = n.data;
    if (n.type !== "start" && !hasIn.has(n.id)) warn(n, "Nothing leads here, so it will never run.");
    switch (n.type) {
      case "start":
      case "message":
        if (d.messageType === "template" && d.templateId) {
          const count = paramCount(d.templateInfo?.body);
          if (Array.from({ length: count }, (_, i) => d.templateParameters?.[i]).some(blank)) bad(n, "Fill in every template value.");
        } else if (blank(d.text)) bad(n, "Write a message or choose a template.");
        break;
      case "buttons":
      case "list": {
        const isList = n.type === "list";
        const noun = isList ? "row" : "button";
        if (blank(d.text)) bad(n, "Write the question or intro text.");
        if (isList && blank(d.buttonText)) bad(n, "Add text for the button that opens the list.");
        if (!d.options.length) bad(n, `Add at least one ${noun}.`);
        if (d.options.some((o) => blank(o.title))) bad(n, `Every ${noun} needs a title.`);
        d.options.forEach((o) => { if (!blank(o.title) && !leadsOn(n, o.id)) warn(n, `"${o.title}" doesn't lead anywhere yet.`); });
        break;
      }
      case "ask":
      case "media":
        if (blank(d.text)) bad(n, "Write what to ask.");
        if (blank(d.variableName)) bad(n, "Choose a name to save the answer under.");
        break;
      case "condition":
        if (d.condition === "variable" && blank(d.variableName)) bad(n, "Enter the saved answer's name.");
        if (!YES_NO.includes(d.condition) && !NO_VALUE.includes(d.operator) && blank(d.value)) bad(n, "Enter a value to compare with.");
        break;
      case "hours":
        if (blank(d.startTime) || blank(d.endTime)) bad(n, "Set opening and closing time.");
        if (!leadsOn(n, HOURS_OPEN) && !leadsOn(n, HOURS_CLOSED)) warn(n, "Connect the Open and Closed outputs.");
        break;
      case "saveValue":
        if (blank(d.variableName)) bad(n, "Choose a name for the saved value.");
        break;
      case "http":
        if (blank(d.url)) bad(n, "Enter the API address (URL).");
        if (d.method !== "GET") {
          try { JSON.parse(d.bodyText || "{}"); } catch { bad(n, "The data to send must be valid JSON. Put {{variables}} inside quotes."); }
        }
        break;
      default:
    }
  });
  return { errors, warnings };
}

/* ───────────────────────── actions shared with nodes ───────────────────────── */
const Ctx = createContext(null);
const useAct = () => useContext(Ctx);

/* ───────────────────────── small form pieces ───────────────────────── */
const Lab = ({ children }) => <div className="fb-lab">{children}</div>;
const Note = ({ children }) => <div className="fb-note">{children}</div>;
const Text = ({ value, onChange, ...p }) => (
  <input className="fb-in nodrag" value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...p} />
);
const Area = ({ value, onChange, bubble, rows = 3, ...p }) => (
  <textarea className={`fb-in fb-ta nodrag nowheel ${bubble ? "fb-bubble" : ""}`} rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...p} />
);
const Pick = ({ value, onChange, options }) => (
  <select className="fb-in nodrag" value={value} onChange={(e) => onChange(e.target.value)}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);
const VarInput = ({ value, onChange }) => (
  <>
    <Text value={value} onChange={(v) => onChange(slug(v))} placeholder="customer_name" />
    <Note>Use it later in any message as {`{{${value || "customer_name"}}}`}</Note>
  </>
);

/* ───────────────────────── step bodies ───────────────────────── */
const MessageBody = ({ id, d }) => {
  const { patch, openTemplate } = useAct();
  if (d.messageType === "template" && d.templateId) {
    const info = d.templateInfo;
    const count = paramCount(info?.body);
    return (
      <>
        <div className="fb-tpl">
          <div className="fb-tpl-name">{d.templateName || "Saved template"}</div>
          {info ? (
            <>
              {info.headerFormat && info.headerFormat !== "TEXT" && <div className="fb-tpl-media">{info.headerFormat.toLowerCase()} header</div>}
              <div className="fb-tpl-body">{info.body}</div>
              {info.buttons.length > 0 && <div className="fb-tpl-btns">{info.buttons.map((b, i) => <span key={i}>{b}</span>)}</div>}
            </>
          ) : <div className="fb-muted">Loading template…</div>}
        </div>
        {Array.from({ length: count }, (_, i) => (
          <React.Fragment key={i}>
            <Lab>{`Value for {{${i + 1}}}`}</Lab>
            <Text
              value={d.templateParameters?.[i] || ""}
              placeholder="e.g. {{customer_name}}"
              onChange={(v) => patch(id, (cur) => {
                const p = Array.from({ length: count }, (_, j) => cur.templateParameters?.[j] || "");
                p[i] = v;
                return { templateParameters: p };
              })}
            />
          </React.Fragment>
        ))}
        {info?.buttons.length > 0 && <Note>Template buttons can't split the flow. To branch on a tap, use a Buttons step.</Note>}
        <div className="fb-links">
          <button className="fb-link nodrag" onClick={() => openTemplate(id)}>Change template</button>
          <button className="fb-link nodrag" onClick={() => patch(id, { messageType: "text" })}>Use plain text</button>
        </div>
      </>
    );
  }
  return (
    <>
      <Area bubble value={d.text} onChange={(v) => patch(id, { text: v })} placeholder="Type your message. Use {{customer_name}} to insert a saved answer." />
      <div className="fb-links">
        <button className="fb-link nodrag" onClick={() => openTemplate(id)}>Use a saved template instead</button>
      </div>
    </>
  );
};

const OptionRows = ({ id, d, isList }) => {
  const { patch, removeOption } = useAct();
  const max = isList ? LIMITS.rows : LIMITS.buttons;
  const noun = isList ? "row" : "button";
  const setOpt = (i, change) => patch(id, (cur) => ({ options: cur.options.map((o, j) => (j === i ? { ...o, ...change } : o)) }));
  return (
    <>
      {d.options.map((o, i) => (
        <div className="fb-opt" key={o.id}>
          <div className="fb-row">
            <Text
              value={o.title}
              maxLength={isList ? LIMITS.rowTitle : LIMITS.buttonTitle}
              placeholder={`${isList ? "Row" : "Button"} ${i + 1}`}
              onChange={(v) => setOpt(i, { title: v })}
            />
            <button className="fb-x nodrag" title={`Remove ${noun}`} onClick={() => removeOption(id, o.id)}>✕</button>
            <Handle type="source" position={Position.Right} id={o.id} />
          </div>
          {isList && (
            <Text value={o.description} maxLength={LIMITS.rowDesc} placeholder="Short description (optional)" onChange={(v) => setOpt(i, { description: v })} />
          )}
        </div>
      ))}
      <button
        className="fb-add nodrag"
        disabled={d.options.length >= max}
        onClick={() => patch(id, (cur) => ({ options: [...cur.options, { id: uid(isList ? "item" : "btn"), title: `${isList ? "Item" : "Option"} ${cur.options.length + 1}`, description: "" }] }))}
      >
        {d.options.length >= max ? `Maximum ${max} ${noun}s` : `+ Add ${noun}`}
      </button>
    </>
  );
};

const NodeBody = ({ id, type, d }) => {
  const { patch } = useAct();
  const set = (k) => (v) => patch(id, { [k]: v });

  switch (type) {
    case "start":
    case "message":
      return <MessageBody id={id} d={d} />;

    case "buttons":
      return (
        <>
          <Area bubble value={d.text} onChange={set("text")} placeholder="Your question" />
          <Lab>Buttons (up to {LIMITS.buttons}, {LIMITS.buttonTitle} characters each)</Lab>
          <OptionRows id={id} d={d} />
          <Note>If the customer types something else instead, the chat restarts from Start.</Note>
        </>
      );

    case "list":
      return (
        <>
          <Area bubble value={d.text} onChange={set("text")} placeholder="Text shown above the menu" />
          <Lab>Text on the button that opens the menu</Lab>
          <Text value={d.buttonText} maxLength={LIMITS.listButton} onChange={set("buttonText")} placeholder="Choose" />
          <Lab>Menu heading (optional)</Lab>
          <Text value={d.sectionTitle} maxLength={24} onChange={set("sectionTitle")} placeholder="e.g. Our courses" />
          <Lab>Rows (up to {LIMITS.rows})</Lab>
          <OptionRows id={id} d={d} isList />
        </>
      );

    case "ask":
    case "media":
      return (
        <>
          <Area bubble value={d.text} onChange={set("text")} placeholder={type === "ask" ? "What do you want to ask?" : "What file do you need?"} />
          <Lab>Save the {type === "ask" ? "answer" : "file"} as</Lab>
          <VarInput value={d.variableName} onChange={set("variableName")} />
          <Note>{type === "ask" ? "The bot waits for the customer's typed reply." : "The bot waits for an image, document, video or audio."}</Note>
        </>
      );

    case "condition": {
      const yesNo = YES_NO.includes(d.condition);
      const noValue = yesNo || NO_VALUE.includes(d.operator);
      return (
        <>
          <Lab>Continue only if…</Lab>
          <Pick value={d.condition} onChange={set("condition")} options={CONDITIONS} />
          {d.condition === "variable" && (
            <>
              <Lab>Saved answer name</Lab>
              <Text value={d.variableName} onChange={(v) => patch(id, { variableName: slug(v) })} placeholder="customer_name" />
            </>
          )}
          {yesNo ? <Note>…is true.</Note> : (
            <>
              <Lab>Rule</Lab>
              <Pick value={d.operator} onChange={set("operator")} options={OPERATORS} />
              {!noValue && (
                <>
                  <Lab>Compare with</Lab>
                  <Text value={d.value} onChange={set("value")} placeholder="e.g. course" />
                </>
              )}
              {d.operator === "regex" && (
                <>
                  <Lab>Regex flags</Lab>
                  <Text value={d.regexFlags} onChange={set("regexFlags")} placeholder="i" />
                </>
              )}
            </>
          )}
          <Note>If the rule doesn't match, the chat stops here.</Note>
        </>
      );
    }

    case "hours":
      return (
        <>
          <Lab>Time zone</Lab>
          <Pick value={d.timezone} onChange={set("timezone")} options={(TIMEZONES.includes(d.timezone) ? TIMEZONES : [d.timezone, ...TIMEZONES]).map((t) => [t, t])} />
          <div className="fb-two">
            <div><Lab>Opens</Lab><Text type="time" value={d.startTime} onChange={set("startTime")} /></div>
            <div><Lab>Closes</Lab><Text type="time" value={d.endTime} onChange={set("endTime")} /></div>
          </div>
          <Lab>Days</Lab>
          <Pick value={d.weekdays} onChange={set("weekdays")} options={WEEKDAYS.some(([v]) => v === d.weekdays) ? WEEKDAYS : [[d.weekdays, d.weekdays], ...WEEKDAYS]} />
          <div className="fb-outs">
            <div className="fb-row fb-out">Open<Handle type="source" position={Position.Right} id={HOURS_OPEN} /></div>
            <div className="fb-row fb-out">Closed<Handle type="source" position={Position.Right} id={HOURS_CLOSED} /></div>
          </div>
        </>
      );

    case "saveValue":
      return (
        <>
          <Lab>Save as</Lab>
          <VarInput value={d.variableName} onChange={set("variableName")} />
          <Lab>What to save</Lab>
          <Pick value={d.source} onChange={set("source")} options={[["message", "The customer's last message"], ["reply", "The button or list item they tapped"]]} />
        </>
      );

    case "http": {
      const setHeader = (i, change) => patch(id, (cur) => ({ headers: cur.headers.map((h, j) => (j === i ? { ...h, ...change } : h)) }));
      return (
        <>
          <Lab>Address</Lab>
          <div className="fb-row">
            <select className="fb-in fb-method nodrag" value={d.method} onChange={(e) => set("method")(e.target.value)}>
              {["GET", "POST", "PUT"].map((m) => <option key={m}>{m}</option>)}
            </select>
            <Text value={d.url} onChange={set("url")} placeholder="https://api.example.com/leads" />
          </div>
          <Lab>Headers</Lab>
          {d.headers.map((h, i) => (
            <div className="fb-row" key={i}>
              <Text value={h.k} onChange={(v) => setHeader(i, { k: v })} placeholder="Name" />
              <Text value={h.v} onChange={(v) => setHeader(i, { v })} placeholder="Value" />
              <button className="fb-x nodrag" title="Remove header" onClick={() => patch(id, (cur) => ({ headers: cur.headers.filter((_, j) => j !== i) }))}>✕</button>
            </div>
          ))}
          <button className="fb-add nodrag" onClick={() => patch(id, (cur) => ({ headers: [...cur.headers, { k: "", v: "" }] }))}>+ Add header</button>
          {d.method !== "GET" && (
            <>
              <Lab>Data to send (JSON)</Lab>
              <Area className="fb-mono" rows={4} value={d.bodyText} onChange={set("bodyText")} placeholder={'{"name": "{{customer_name}}"}'} />
            </>
          )}
          <Note>You can use {"{{variables}}"} in the address, headers and data. Nothing is sent to the customer.</Note>
        </>
      );
    }

    case "handoff":
      return (
        <>
          <Area bubble value={d.text} onChange={set("text")} placeholder="Message before the agent joins (optional)" />
          <Lab>Department (optional)</Lab>
          <Text value={d.department} onChange={set("department")} placeholder="e.g. sales" />
          <Lab>Sub-department (optional)</Lab>
          <Text value={d.subDepartment} onChange={set("subDepartment")} placeholder="e.g. enquiries" />
          <Lab>Priority</Lab>
          <Pick value={d.priority} onChange={set("priority")} options={[["low", "Low"], ["normal", "Normal"], ["high", "High"]]} />
          <Note>The bot pauses and the chat goes to your agent inbox.</Note>
        </>
      );

    default:
      return (
        <>
          <Note>This step ({d.apiType}) was made outside the builder. It is kept exactly as it is when you save.</Note>
          {(d.extraHandles || []).map((h) => (
            <div className="fb-row fb-out" key={h}>{h}<Handle type="source" position={Position.Right} id={h} /></div>
          ))}
        </>
      );
  }
};

const FlowNode = ({ id, type, data, selected }) => {
  const k = KINDS[type] || RAW;
  const { remove, endIds } = useAct();
  const ownOutputs = ["buttons", "list", "hours", "handoff"].includes(type);
  return (
    <div className={`fb-node ${selected ? "sel" : ""}`} style={{ "--c": k.color }}>
      {type !== "start" && <Handle type="target" position={Position.Left} />}
      <div className="fb-head">
        <span className="fb-ico">{k.icon}</span>
        <span className="fb-title">{k.title}</span>
        {endIds.has(id) && type !== "handoff" && <span className="fb-end">Chat ends here</span>}
        <button className="fb-x nodrag" title="Delete step" onClick={() => remove(id)}>✕</button>
      </div>
      <div className="fb-body"><NodeBody id={id} type={type} d={data} /></div>
      {!ownOutputs && <Handle type="source" position={Position.Right} />}
    </div>
  );
};

const nodeTypes = { ...Object.fromEntries(Object.keys(KINDS).map((k) => [k, FlowNode])), raw: FlowNode };

/* ───────────────────────── modal ───────────────────────── */
const Modal = ({ open, title, onClose, footer, children }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fb-ov" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="fb-modal" role="dialog" aria-label={title}>
        <div className="fb-mh"><h2>{title}</h2><button className="fb-btn" onClick={onClose}>Close</button></div>
        <div className="fb-mb">{children}</div>
        {footer && <div className="fb-mf">{footer}</div>}
      </div>
    </div>
  );
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

/* ───────────────────────── main builder ───────────────────────── */
const FlowBuilder = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const wrapRef = useRef(null);
  const toastTimer = useRef(null);
  const skipApply = useRef(null);

  const [rfi, setRfi] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const [customerId, setCustomerId] = useState(params.get("customer_id") || null);
  const [phoneNumberId, setPhoneNumberId] = useState(params.get("phone_number_id") || "");
  const [workflowId, setWorkflowId] = useState(params.get("workflow_id") || "");
  const [flowName, setFlowName] = useState("My chatbot");
  const [flowDesc, setFlowDesc] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  const [status, setStatus] = useState(null);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [tplTarget, setTplTarget] = useState(null);
  const [selTpl, setSelTpl] = useState(null);
  const [tplSearch, setTplSearch] = useState("");

  const toast = useCallback((type, msg) => {
    setStatus({ type, msg });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setStatus(null), 5000);
  }, []);

  /* ── data ── */
  const { data: listData, isLoading: listLoading, isError: listError, refetch: refetchList } = useGetwhatsappchatflowlistQuery(
    { phone_number_id: phoneNumberId, customer_id: customerId },
    { skip: !listOpen || !phoneNumberId || !customerId }
  );
  const { data: selectedFlowData, isFetching: flowLoading, isError: flowError } = useSelectwhatsappchatflowQuery(
    { phone_number_id: phoneNumberId, customer_id: customerId, workflow_id: workflowId },
    { skip: !phoneNumberId || !customerId || !workflowId }
  );
  const [fetchFlow] = useLazySelectwhatsappchatflowQuery();
  const [getPhoneNumbers, { data: phoneData }] = useLazyGetPhoneNUmberQuery();
  const [createChatbot, { isLoading: saving }] = useWhatsappchatbotcreateMutation();
  const { data: templateData, isLoading: tplLoading, isError: tplError } = useGetTemplatesQuery(
    { customer_id: customerId },
    { skip: !customerId }
  );

  const phones = phoneData?.phone_numbers || [];
  const workflows = listData?.workflows || [];
  const approvedTemplates = useMemo(
    () => (templateData?.templates ?? [])
      .filter((t) => t.status?.toUpperCase() === "APPROVED")
      .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [templateData]
  );
  const templateKinds = useMemo(() => Object.fromEntries((templateData?.template_requests ?? []).map((r) => [r.template_id, r.template_type])), [templateData]);

  /* customer id comes from the login token */
  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) { if (!customerId) toast("error", "You are not signed in. Please log in again."); return; }
      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (id) setCustomerId(id);
      else if (!customerId) toast("error", "Customer ID not found in your login.");
    } catch {
      if (!customerId) toast("error", "Your login looks invalid. Please sign in again.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (customerId) getPhoneNumbers(customerId); }, [customerId, getPhoneNumbers]);

  useEffect(() => {
    if (!phoneNumberId && phones.length === 1) setPhoneNumberId(phones[0].phone_number_id);
  }, [phones, phoneNumberId]);

  /* fill in template previews for steps loaded from a saved flow */
  useEffect(() => {
    if (!approvedTemplates.length || !nodes.some((n) => n.data?.templateId && !n.data.templateInfo)) return;
    setNodes((ns) => ns.map((n) => {
      if (!n.data?.templateId || n.data.templateInfo) return n;
      const t = approvedTemplates.find((x) => x.template_id === n.data.templateId);
      const info = t ? templateInfoOf(t) : { body: "This template was not found or is no longer approved.", headerFormat: "", buttons: [] };
      return { ...n, data: { ...n.data, templateName: t?.name || n.data.templateId, templateInfo: info } };
    }));
  }, [approvedTemplates, nodes, setNodes]);

  /* ── step actions ── */
  const patch = useCallback((id, change) => {
    setNodes((ns) => ns.map((n) => (n.id !== id ? n : { ...n, data: { ...n.data, ...(typeof change === "function" ? change(n.data) : change) } })));
  }, [setNodes]);

  const remove = useCallback((id) => {
    setNodes((ns) => ns.filter((n) => n.id !== id));
    setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
  }, [setNodes, setEdges]);

  const removeOption = useCallback((id, optId) => {
    patch(id, (cur) => ({ options: cur.options.filter((o) => o.id !== optId) }));
    setEdges((es) => es.filter((e) => !(e.source === id && e.sourceHandle === optId)));
  }, [patch, setEdges]);

  const openTemplate = useCallback((id) => { setTplTarget(id); setSelTpl(null); setTplSearch(""); }, []);
  const closeTemplate = useCallback(() => { setTplTarget(null); setSelTpl(null); }, []);

  const applyTemplate = (t) => {
    const info = templateInfoOf(t);
    const count = paramCount(info.body);
    patch(tplTarget, (cur) => ({
      messageType: "template",
      templateId: t.template_id,
      templateName: t.name,
      templateInfo: info,
      templateParameters: Array.from({ length: count }, (_, i) => (cur.templateId === t.template_id ? cur.templateParameters?.[i] || "" : "")),
    }));
    closeTemplate();
  };

  const endIds = useMemo(() => {
    const hasOut = new Set(edges.map((e) => e.source));
    return new Set(nodes.filter((n) => !hasOut.has(n.id)).map((n) => n.id));
  }, [nodes, edges]);

  const actions = useMemo(() => ({ patch, remove, removeOption, openTemplate, endIds }), [patch, remove, removeOption, openTemplate, endIds]);

  /* ── canvas ── */
  const toFlow = (x, y) => {
    if (!rfi) return { x: 0, y: 0 };
    if (rfi.screenToFlowPosition) return rfi.screenToFlowPosition({ x, y });
    const b = wrapRef.current.getBoundingClientRect();
    return rfi.project({ x: x - b.left, y: y - b.top });
  };

  const addStep = (kind, position) => {
    if (kind === "start" && nodes.some((n) => n.type === "start")) { toast("error", "Your flow already has a Start step."); return; }
    setNodes((ns) => [...ns, mk(kind, position)]);
  };

  const addAtCenter = (kind) => {
    const b = wrapRef.current.getBoundingClientRect();
    const jitter = (nodes.length % 6) * 28;
    addStep(kind, toFlow(b.left + b.width / 2 - 145 + jitter, b.top + b.height / 3 + jitter));
  };

  const onDrop = (e) => {
    e.preventDefault();
    const kind = e.dataTransfer.getData("application/reactflow");
    if (kind && KINDS[kind]) addStep(kind, toFlow(e.clientX, e.clientY));
  };

  /* one next step per output: connecting again replaces the old line */
  const onConnect = useCallback((c) => {
    if (c.source === c.target) return;
    setEdges((es) => addEdge(
      { source: c.source, target: c.target, sourceHandle: c.sourceHandle ?? null, targetHandle: c.targetHandle ?? null },
      es.filter((e) => !(e.source === c.source && (e.sourceHandle || null) === (c.sourceHandle || null)))
    ));
  }, [setEdges]);

  const shownEdges = useMemo(() => edges.map((e) => {
    const src = nodes.find((n) => n.id === e.source);
    let label;
    if (e.sourceHandle && src) {
      if (src.type === "buttons" || src.type === "list") label = src.data.options.find((o) => o.id === e.sourceHandle)?.title;
      else if (src.type === "hours") label = e.sourceHandle === HOURS_OPEN ? "Open" : "Closed";
      else label = e.sourceHandle;
    }
    return {
      ...e,
      type: "smoothstep",
      label,
      labelStyle: { fontSize: 11, fontWeight: 600, fill: "#2c3a36" },
      labelBgStyle: { fill: "#fff", stroke: "#d5dcd9" },
      labelBgPadding: [6, 3],
      labelBgBorderRadius: 6,
      markerEnd: { type: MarkerType.ArrowClosed, color: EDGE_COLOR },
    };
  }), [edges, nodes]);

  const fit = () => setTimeout(() => rfi?.fitView({ padding: 0.2, maxZoom: 1 }), 60);

  const focusNode = (id) => {
    if (!id || !rfi) return;
    setNodes((ns) => ns.map((n) => ({ ...n, selected: n.id === id })));
    rfi.fitView({ nodes: [{ id }], duration: 300, maxZoom: 1, padding: 0.5 });
  };

  /* ── load / save ── */
  const applyFlow = (data) => {
    const loaded = loadFlow(data);
    if (!loaded) { toast("error", "This flow has no steps to show."); return; }
    setNodes(loaded.nodes);
    setEdges(loaded.edges);
    if (data.name) setFlowName(data.name);
    setFlowDesc(data.description || "");
    // status: from the flow itself, else from the "My flows" list, else assume active
    const known = data.is_active ?? workflows.find((w) => w.workflow_id === workflowId)?.is_active;
    setIsActive(known ?? true);
    toast(loaded.dropped ? "error" : "success",
      loaded.dropped
        ? `Opened "${data.name || "flow"}", but ${loaded.dropped} connection(s) could not be restored.`
        : `Opened "${data.name || "flow"}".`);
    fit();
  };

  useEffect(() => {
    if (!selectedFlowData?.workflow_config) return;
    if (skipApply.current && skipApply.current === workflowId) { skipApply.current = null; return; }
    applyFlow(selectedFlowData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFlowData]);

  const loadSpecific = (wid) => {
    setListOpen(false);
    skipApply.current = null;
    if (wid === workflowId && selectedFlowData) applyFlow(selectedFlowData);
    else setWorkflowId(wid);
  };

  /* Turn a flow on or off from "My flows".
     We fetch the saved flow from the server and re-post it unchanged with only is_active flipped,
     so nothing can be lost and unsaved canvas edits are never saved by accident.
     Turning one ON turns the others off (server rule: one active flow per number). */
  const toggleActive = async (f) => {
    const next = !f.is_active;
    if (!next && !window.confirm(`Turn off "${f.name}"? The bot will stop replying on this number.`)) return;

    setTogglingId(f.workflow_id);
    try {
      const full = await fetchFlow(
        { phone_number_id: phoneNumberId, customer_id: customerId, workflow_id: f.workflow_id },
        false
      ).unwrap();

      if (f.workflow_id === workflowId) skipApply.current = workflowId; // keep unsaved edits on the canvas
      await createChatbot({
        phone_number_id: phoneNumberId,
        customer_id: customerId,
        workflow_id: f.workflow_id,
        name: full.name,
        description: full.description || "",
        is_active: next,
        workflow_config: full.workflow_config,
      }).unwrap();

      await refetchList(); // shows what the server really did (others switch off when one turns on)
      if (f.workflow_id === workflowId) setIsActive(next);
      toast("success", next ? `"${f.name}" is now active.` : `"${f.name}" is now inactive.`);
    } catch (err) {
      toast("error", err?.data?.message || err?.error || "Could not change the status.");
    } finally {
      setTogglingId(null);
    }
  };

  const { errors, warnings } = useMemo(() => inspect(nodes, edges), [nodes, edges]);

  const save = async (asCopy = false) => {
    if (!customerId || !phoneNumberId) { toast("error", "Choose a WhatsApp number first."); return; }
    if (errors.length) { toast("error", `Fix ${errors.length} thing${errors.length > 1 ? "s" : ""} in the checklist first.`); return; }
    try {
      // editing keeps the flow's current status; new flows and copies start active
      const keep = workflowId && !asCopy ? isActive : true;
      const payload = buildPayload(flowName, flowDesc, nodes, edges, keep);
      if (workflowId && !asCopy) payload.workflow_id = workflowId;
      const result = await createChatbot({ phone_number_id: phoneNumberId, customer_id: customerId, ...payload }).unwrap();
      if (result?.workflow_id) { skipApply.current = result.workflow_id; setWorkflowId(result.workflow_id); }
      if (!workflowId || asCopy) setIsActive(true);
      toast("success", keep ? "Saved. This is the active flow for this number." : "Saved. This flow is still inactive.");
    } catch (err) {
      toast("error", err?.data?.message || err?.error || "Could not save. Please try again.");
    }
  };

  const newFlow = () => {
    if (nodes.length && !window.confirm("Start a new flow? Unsaved changes will be lost.")) return;
    skipApply.current = null;
    setNodes([]); setEdges([]); setWorkflowId(""); setFlowName("My chatbot"); setFlowDesc(""); setIsActive(true);
  };

  const useStarter = () => {
    const s = mk("start", { x: 0, y: 100 });
    const b = mk("buttons", { x: 400, y: 60 });
    const m = mk("message", { x: 820, y: 0 });
    const h = mk("handoff", { x: 820, y: 300 });
    b.data.options = mkOpts("btn", ["Pricing", "Talk to us"]);
    m.data.text = "Our plans start at ₹999/month. Reply anytime if you have questions!";
    setNodes([s, b, m, h]);
    setEdges([
      { id: uid("edge"), source: s.id, target: b.id, sourceHandle: null },
      { id: uid("edge"), source: b.id, target: m.id, sourceHandle: b.data.options[0].id },
      { id: uid("edge"), source: b.id, target: h.id, sourceHandle: b.data.options[1].id },
    ]);
    fit();
  };

  const json = useMemo(
    () => (jsonOpen ? JSON.stringify(buildPayload(flowName, flowDesc, nodes, edges, isActive), null, 2) : ""),
    [jsonOpen, flowName, flowDesc, nodes, edges, isActive]
  );

  const filteredTemplates = approvedTemplates.filter((t) => {
    const q = tplSearch.trim().toLowerCase();
    return !q || (t.name || "").toLowerCase().includes(q) || (comp(t, "BODY")?.text || "").toLowerCase().includes(q);
  });

  const problems = [...errors.map((p) => ({ ...p, t: "error" })), ...warnings.map((p) => ({ ...p, t: "warn" }))];

  return (
    <Ctx.Provider value={actions}>
      <div className="fb">
        <style>{css}</style>

        {/* top bar */}
        <header className="fb-top">
          <input className="fb-name" value={flowName} onChange={(e) => setFlowName(e.target.value)} aria-label="Flow name" />
          {workflowId && <span className={`fb-pill ${isActive ? "on" : ""}`}>{isActive ? "Active" : "Inactive"}</span>}
          <label className="fb-phone">
            WhatsApp number
            <select className="fb-in" style={{ width: 210 }} value={phoneNumberId} onChange={(e) => { setPhoneNumberId(e.target.value); setWorkflowId(""); }}>
              <option value="">Choose a number</option>
              {phones.map((p) => <option key={p.phone_number_id} value={p.phone_number_id}>{p.display_phone_number || p.phone_number_id}</option>)}
              {phoneNumberId && !phones.some((p) => p.phone_number_id === phoneNumberId) && <option value={phoneNumberId}>{phoneNumberId}</option>}
            </select>
          </label>
          <div style={{ flex: 1 }} />
          {status && <div className={`fb-toast ${status.type}`} role="status">{status.msg}</div>}
          <button className="fb-btn" onClick={() => { if (!phoneNumberId) toast("error", "Choose a WhatsApp number first."); else setListOpen(true); }}>My flows</button>
          <button className="fb-btn" onClick={newFlow}>New</button>
          <button className="fb-btn" onClick={() => setJsonOpen(true)}>View JSON</button>
          {workflowId && <button className="fb-btn" disabled={saving} onClick={() => save(true)}>Save as copy</button>}
          <button className="fb-btn primary" disabled={saving} onClick={() => save(false)}>{saving ? "Saving…" : workflowId ? "Save changes" : "Save flow"}</button>
        </header>

        <div className="fb-main">
          {/* sidebar */}
          <aside className="fb-side">
            <p className="fb-help">Drag a step onto the canvas, or click it. Then drag from the dot on a step's right edge to the next step.</p>
            {GROUPS.map((g) => (
              <section key={g}>
                <div className="fb-gt">{g}</div>
                {Object.entries(KINDS).filter(([, k]) => k.group === g).map(([kind, k]) => (
                  <button
                    key={kind}
                    className="fb-pal"
                    style={{ "--c": k.color }}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData("application/reactflow", kind); e.dataTransfer.effectAllowed = "move"; }}
                    onClick={() => addAtCenter(kind)}
                  >
                    <span className="fb-ico">{k.icon}</span>
                    <span><b>{k.title}</b><small>{k.hint}</small></span>
                  </button>
                ))}
              </section>
            ))}

            <div className="fb-gt">About this flow</div>
            <textarea className="fb-in fb-ta" rows={2} value={flowDesc} onChange={(e) => setFlowDesc(e.target.value)} placeholder="Short description (optional)" />

            <div className="fb-gt">Checklist</div>
            {nodes.length === 0 ? <div className="fb-muted">Add a Start step to begin.</div>
              : problems.length === 0 ? <div className="fb-ok">✓ Everything looks good.</div>
              : problems.map((p, i) => (
                <button key={i} className={`fb-ck ${p.t}`} onClick={() => focusNode(p.id)}>
                  <span aria-hidden="true">{p.t === "error" ? "●" : "○"}</span>{p.msg}
                </button>
              ))}
            {problems.length > 0 && <div className="fb-muted" style={{ marginTop: 8 }}>● must be fixed to save · ○ worth a look</div>}
          </aside>

          {/* canvas */}
          <main className="fb-canvas" ref={wrapRef}>
            <ReactFlow
              nodes={nodes}
              edges={shownEdges}
              nodeTypes={nodeTypes}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onInit={setRfi}
              onDrop={onDrop}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              deleteKeyCode={["Backspace", "Delete"]}
              minZoom={0.25}
              fitView
            >
              <Background gap={22} size={1.4} color="#c3cfca" />
              <Controls showInteractive={false} />
            </ReactFlow>

            {nodes.length === 0 && !flowLoading && (
              <div className="fb-empty">
                <h2>Build your WhatsApp chatbot</h2>
                <p>Add a Start step from the left, or begin with a ready-made example you can edit.</p>
                <button className="fb-btn primary" onClick={useStarter}>Use an example flow</button>
              </div>
            )}
            {flowLoading && <div className="fb-empty solid"><h2>Opening flow…</h2></div>}
            {flowError && <div className="fb-toast error fb-corner">Could not open this flow. Check the number and try again.</div>}
          </main>
        </div>

        {/* template picker */}
        <Modal
          open={!!tplTarget}
          title="Choose a saved template"
          onClose={closeTemplate}
          footer={<button className="fb-btn primary" disabled={!selTpl} onClick={() => applyTemplate(approvedTemplates.find((t) => t.template_id === selTpl))}>Use this template</button>}
        >
          <input className="fb-in" type="search" value={tplSearch} onChange={(e) => setTplSearch(e.target.value)} placeholder="Search by name or text" />
          {tplLoading && <div className="fb-muted" style={{ marginTop: 12 }}>Loading templates…</div>}
          {tplError && <div className="fb-muted" style={{ marginTop: 12, color: "var(--danger)" }}>Could not load templates.</div>}
          {!tplLoading && filteredTemplates.length === 0 && <div className="fb-muted" style={{ marginTop: 12 }}>No approved templates found.</div>}
          <div className="fb-tcs">
            {filteredTemplates.map((t) => {
              const info = templateInfoOf(t);
              return (
                <button key={t.template_id} className={`fb-tc ${selTpl === t.template_id ? "on" : ""}`} onClick={() => setSelTpl(t.template_id)}>
                  <span className="fb-tc-h"><b>{t.name}</b><span>{[templateKinds[t.template_id] || t.category, t.language].filter(Boolean).join(" · ")}</span></span>
                  {info.headerFormat && info.headerFormat !== "TEXT" && <span className="fb-tpl-media">{info.headerFormat.toLowerCase()} header</span>}
                  <span className="fb-tc-b">{info.body}</span>
                  {info.buttons.length > 0 && <span className="fb-tpl-btns">{info.buttons.map((b, i) => <span key={i}>{b}</span>)}</span>}
                </button>
              );
            })}
          </div>
        </Modal>

        {/* my flows */}
        <Modal open={listOpen} title="My flows" onClose={() => setListOpen(false)}>
          {listLoading && <div className="fb-muted">Loading flows…</div>}
          {listError && <div className="fb-muted" style={{ color: "var(--danger)" }}>Could not load your flows.</div>}
          {!listLoading && !listError && workflows.length === 0 && <div className="fb-muted">No flows yet for this number.</div>}
          {!listLoading && workflows.length > 0 && (
            <div className="fb-muted" style={{ marginBottom: 10 }}>Only one flow can be active at a time. Turning one on turns the others off.</div>
          )}
          {workflows.map((f) => (
            <div key={f.workflow_id} className={`fb-li ${f.workflow_id === workflowId ? "on" : ""}`}>
              <button className="fb-li-main" onClick={() => loadSpecific(f.workflow_id)}>
                <span className="fb-li-t">
                  <b>{f.name || "Unnamed flow"}</b>
                  <span className={`fb-pill ${f.is_active ? "on" : ""}`}>{f.is_active ? "Active" : "Inactive"}</span>
                </span>
                <span>{f.description || "No description"}</span>
                <small>Created {formatDate(f.created_at)} · Updated {formatDate(f.updated_at)}</small>
              </button>
              <button
                role="switch"
                aria-checked={!!f.is_active}
                aria-label={`${f.is_active ? "Turn off" : "Turn on"} ${f.name || "flow"}`}
                title={f.is_active ? "Turn off" : "Turn on (turns the others off)"}
                className={`fb-sw ${f.is_active ? "on" : ""}`}
                disabled={togglingId !== null}
                onClick={() => toggleActive(f)}
              />
            </div>
          ))}
        </Modal>

        {/* json */}
        <Modal
          open={jsonOpen}
          title="Flow JSON"
          onClose={() => setJsonOpen(false)}
          footer={<button className="fb-btn" onClick={() => navigator.clipboard?.writeText(json).then(() => toast("success", "Copied."))}>Copy to clipboard</button>}
        >
          <pre className="fb-json">{json}</pre>
        </Modal>
      </div>
    </Ctx.Provider>
  );
};

const ChatbotFlowBuilderPage = () => (
  <ReactFlowProvider>
    <FlowBuilder />
  </ReactFlowProvider>
);

export default ChatbotFlowBuilderPage;

/* ───────────────────────── styles (scoped to .fb) ───────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700&display=swap');
.fb{--bg:#edf1ef;--ink:#14201d;--muted:#5b6a65;--line:#d5dcd9;--soft:#f4f7f6;--brand:#0b7a6b;--danger:#b42318;--warn:#a15c07;
  height:100vh;display:flex;flex-direction:column;background:var(--bg);color:var(--ink);font-family:'Figtree',system-ui,sans-serif;font-size:14px}
.fb *,.fb *::before,.fb *::after{box-sizing:border-box}
.fb h2{margin:0}
.fb button{font:inherit;color:inherit;cursor:pointer}
.fb button:disabled{cursor:not-allowed;opacity:.5}
.fb :focus-visible{outline:2px solid var(--brand);outline-offset:2px}

.fb-top{height:58px;flex:none;display:flex;align-items:center;gap:10px;padding:0 16px;background:#fff;border-bottom:1px solid var(--line);position:relative;z-index:5}
.fb-name{width:220px;font:700 16px 'Figtree',sans-serif;color:var(--ink);border:1px solid transparent;border-radius:8px;padding:6px 8px;background:transparent}
.fb-name:hover,.fb-name:focus{border-color:var(--line);background:#fff}
.fb-phone{display:flex;align-items:center;gap:8px;color:var(--muted);font-size:13px}
.fb-btn{border:1px solid var(--line);background:#fff;border-radius:9px;padding:8px 13px;font-weight:600;font-size:13px}
.fb-btn:hover:not(:disabled){background:var(--soft)}
.fb-btn.primary{background:var(--brand);border-color:var(--brand);color:#fff}
.fb-btn.primary:hover:not(:disabled){background:#09665a}
.fb-toast{max-width:380px;padding:7px 12px;border-radius:9px;font-size:13px;font-weight:500;border:1px solid}
.fb-toast.success{background:#e6f5ef;border-color:#9fd6c2;color:#0a5c4f}
.fb-toast.error{background:#fdecea;border-color:#f3b4ae;color:var(--danger)}
.fb-corner{position:absolute;bottom:16px;left:50%;transform:translateX(-50%);z-index:5}

.fb-main{flex:1;display:flex;min-height:0}
.fb-side{width:276px;flex:none;background:#fff;border-right:1px solid var(--line);overflow-y:auto;padding:14px 12px 24px}
.fb-help{margin:0 4px 4px;color:var(--muted);font-size:12.5px;line-height:1.45}
.fb-gt{font-size:12.5px;font-weight:700;color:var(--muted);margin:16px 4px 6px}
.fb-pal{display:flex;gap:10px;align-items:center;width:100%;text-align:left;border:1px solid var(--line);background:#fff;border-radius:11px;padding:8px 10px;margin-bottom:6px;cursor:grab}
.fb-pal:hover{border-color:var(--c);background:var(--soft)}
.fb-pal b{display:block;font-size:13px}
.fb-pal small{display:block;color:var(--muted);font-size:12px;line-height:1.3}
.fb-ico{width:28px;height:28px;flex:none;display:grid;place-items:center;border-radius:8px;font-size:14px;background:color-mix(in srgb,var(--c) 14%,#fff)}
.fb-ok{color:#0a5c4f;font-weight:600;padding:4px}
.fb-muted{color:var(--muted);font-size:12.5px;padding:0 4px}
.fb-ck{display:flex;gap:8px;width:100%;text-align:left;border:0;background:transparent;border-radius:8px;padding:6px 6px;font-size:12.5px;line-height:1.35}
.fb-ck:hover{background:var(--soft)}
.fb-ck.error span{color:var(--danger)}
.fb-ck.warn span{color:var(--warn)}

.fb-canvas{flex:1;position:relative;min-width:0}
.fb .react-flow__edge-path{stroke:${EDGE_COLOR};stroke-width:2}
.fb .react-flow__edge.selected .react-flow__edge-path{stroke:var(--brand);stroke-width:3}
.fb .react-flow__controls{box-shadow:none;border:1px solid var(--line);border-radius:10px;overflow:hidden}
.fb .react-flow__handle{width:12px;height:12px;background:var(--c,#0b7a6b);border:2px solid #fff;box-shadow:0 0 0 1px var(--line)}
.fb .react-flow__handle-left{left:-7px}
.fb .react-flow__handle-right{right:-7px}
.fb-row .react-flow__handle,.fb .fb-row .react-flow__handle-right{right:-19px}

.fb-empty{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;text-align:center;pointer-events:none;padding:24px}
.fb-empty h2{font-size:20px}
.fb-empty p{margin:0;color:var(--muted);max-width:360px}
.fb-empty .fb-btn{pointer-events:auto;margin-top:6px}
.fb-empty.solid{background:rgba(237,241,239,.85);z-index:6}

.fb-node{position:relative;width:292px;background:#fff;border:1.5px solid var(--line);border-radius:14px;box-shadow:0 1px 2px rgba(20,32,29,.06),0 8px 22px rgba(20,32,29,.08)}
.fb-node.sel{border-color:var(--c);box-shadow:0 0 0 3px color-mix(in srgb,var(--c) 22%,transparent),0 8px 22px rgba(20,32,29,.1)}
.fb-head{display:flex;align-items:center;gap:8px;padding:9px 10px 9px 12px;border-bottom:1px solid var(--line)}
.fb-title{font-weight:700;font-size:14px;flex:1}
.fb-end{font-size:11.5px;color:var(--muted);background:var(--soft);border:1px solid var(--line);border-radius:20px;padding:1px 8px}
.fb-x{border:0;background:transparent;color:var(--muted);border-radius:6px;width:24px;height:24px;flex:none;font-size:12px;line-height:1}
.fb-x:hover{background:#fdecea;color:var(--danger)}
.fb-body{padding:4px 12px 12px}
.fb-lab{font-size:12px;font-weight:600;color:var(--muted);margin:10px 0 4px}
.fb-note{font-size:12px;color:var(--muted);line-height:1.4;margin-top:8px}
.fb-in{width:100%;font:inherit;font-size:13px;color:var(--ink);background:#fff;border:1px solid var(--line);border-radius:8px;padding:7px 9px}
.fb-in:focus{border-color:var(--brand)}
.fb-ta{resize:vertical;line-height:1.45}
.fb-bubble{background:#e3f6ea;border-color:#bfe3cd;border-radius:4px 12px 12px 12px}
.fb-mono{font-family:ui-monospace,Menlo,monospace;font-size:12px}
.fb-method{width:84px;flex:none}
.fb-opt{margin-top:6px}
.fb-opt .fb-in+.fb-in{margin-top:4px;font-size:12px}
.fb-row{position:relative;display:flex;align-items:center;gap:6px;margin-top:6px}
.fb-two{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.fb-outs{margin-top:10px;border-top:1px dashed var(--line);padding-top:4px}
.fb-out{justify-content:flex-end;font-weight:600;font-size:13px;padding:5px 0}
.fb-add{width:100%;margin-top:8px;border:1px dashed var(--c,var(--line));background:transparent;color:var(--ink);border-radius:8px;padding:6px;font-size:12.5px;font-weight:600}
.fb-add:hover:not(:disabled){background:var(--soft)}
.fb-links{display:flex;gap:14px;margin-top:8px;flex-wrap:wrap}
.fb-link{border:0;background:none;padding:0;color:var(--brand);font-size:12.5px;font-weight:600;text-decoration:underline}

.fb-tpl{margin-top:6px;border:1px solid var(--line);border-radius:10px;padding:9px 10px;background:var(--soft)}
.fb-tpl-name{font-weight:700;font-size:12.5px;margin-bottom:4px}
.fb-tpl-body{white-space:pre-wrap;font-size:12.5px;line-height:1.45;max-height:120px;overflow:auto}
.fb-tpl-media{display:inline-block;font-size:11.5px;color:var(--muted);border:1px solid var(--line);border-radius:6px;padding:0 6px;margin-bottom:4px;background:#fff}
.fb-tpl-btns{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px}
.fb-tpl-btns span{font-size:12px;font-weight:600;color:#1d6fa5;background:#fff;border:1px solid var(--line);border-radius:7px;padding:2px 8px}

.fb-ov{position:fixed;inset:0;background:rgba(20,32,29,.45);z-index:50;display:grid;place-items:center;padding:16px}
.fb-modal{width:min(720px,100%);max-height:86vh;display:flex;flex-direction:column;background:#fff;border-radius:14px;box-shadow:0 24px 70px rgba(0,0,0,.3)}
.fb-mh{display:flex;align-items:center;justify-content:space-between;padding:14px 18px;border-bottom:1px solid var(--line)}
.fb-mh h2{font-size:16px}
.fb-mb{padding:16px 18px;overflow:auto;flex:1}
.fb-mf{padding:12px 18px;border-top:1px solid var(--line);display:flex;justify-content:flex-end}
.fb-tcs{display:flex;flex-direction:column;gap:8px;margin-top:12px}
.fb-tc,.fb-li{display:flex;flex-direction:column;gap:5px;text-align:left;width:100%;background:#fff;border:1px solid var(--line);border-radius:11px;padding:11px 12px;margin-bottom:8px}
.fb-tc{margin:0}
.fb-tc:hover,.fb-li:hover{background:var(--soft)}
.fb-tc.on,.fb-li.on{border-color:var(--brand);box-shadow:0 0 0 2px color-mix(in srgb,var(--brand) 20%,transparent)}
.fb-tc-h{display:flex;justify-content:space-between;gap:10px}
.fb-tc-h span{color:var(--muted);font-size:12px}
.fb-tc-b{white-space:pre-wrap;font-size:13px;line-height:1.45;display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden}
.fb-li span{color:var(--muted)}
.fb-li small{color:var(--muted);font-size:12px}

/* flow list rows with status + switch (override the stacked layout above) */
.fb-li{flex-direction:row;align-items:center;gap:8px;padding:0 12px 0 0}
.fb-li-main{flex:1;min-width:0;display:flex;flex-direction:column;gap:5px;text-align:left;border:0;background:transparent;padding:11px 12px;border-radius:11px}
.fb-li-t{display:flex;align-items:center;gap:8px;color:var(--ink)}
.fb-pill{font-size:11.5px;font-weight:600;padding:1px 9px;border-radius:20px;border:1px solid var(--line);color:var(--muted);background:var(--soft);white-space:nowrap}
.fb-pill.on{color:#0a5c4f;background:#e6f5ef;border-color:#9fd6c2}
.fb-sw{position:relative;width:40px;height:22px;flex:none;border:0;border-radius:20px;background:#c3cfca;padding:0;transition:background .15s}
.fb-sw::after{content:"";position:absolute;top:3px;left:3px;width:16px;height:16px;border-radius:50%;background:#fff;transition:transform .15s}
.fb-sw.on{background:var(--brand)}
.fb-sw.on::after{transform:translateX(18px)}

.fb-json{margin:0;background:#101a17;color:#bdf0dc;border-radius:10px;padding:14px;font:12px/1.55 ui-monospace,Menlo,monospace;white-space:pre-wrap;word-break:break-word}
@media (prefers-reduced-motion:reduce){.fb *{transition:none!important;animation:none!important}}
`;